/* External */
import {
  memo, useCallback, useEffect, useRef,
} from 'react';
import PropTypes from 'prop-types';

/* Ott-common */
import ProgressBar, { ScrubbingDirection, ScrubbingSpeed } from '@dtvgo/progress-bar';
import {
  usePlayerState, usePlayerActions, PlayerState, useSeeking,
} from '@dtvgo/player-context';

/* Utils */
import withInteractiveFocusable from '../../../../hocs/withInteractiveFocusable.hoc';
import useRefSync from '../../../../hooks/useRefSync';
import { ClassNames } from '../../../../utils/misc';
import { withClassName } from '../../../../hocs/withClassName.hoc';
import platform from '../../../../utils/platform';
import { useOnKeyUp } from '../../../../utils/keyDownEvents';

/* Styles */
import './FocusableProgressBar.scss';

export const FocusableProgressBarClassNames = ClassNames.component('focusable-progress-bar');

function FocusableProgressBarBase({
  className,
  progress,
  duration,
  seekable,
  scrubbing,
  isScrubbing,
  focused,
  cursorVisible,
  onSeek,
  onSeekingStart,
  onSeekingEnd,
  onSeekableStartReached,
  onSeekableEndReached,
  onKeyDown,
  children,
}) {
  const seekableStart = seekable?.start || 0;
  const seekableEnd = seekable?.end || duration;
  const state = usePlayerState();
  const stateRef = useRefSync(state);
  const references = useRef({
    shouldPlayOnSeekingEnd: false,
    scrubInProgress: false,
    rightKeyPressed: false,
    seekableStart,
    seekableEnd,
    blockForwardScrubbing: false,
  });
  const { play, pause } = usePlayerActions();
  const { isSeeking: seeking } = useSeeking();

  const handleSeekingStart = useCallback(() => {
    if (stateRef.current === PlayerState.PAUSED) {
      references.current.shouldPlayOnSeekingEnd = false;
    } else {
      pause();
      references.current.shouldPlayOnSeekingEnd = true;
    }

    references.current.scrubInProgress = true;
    if (onSeekingStart) onSeekingStart();
  }, [onSeekingStart, pause, stateRef]);

  const handleSeekingEnd = useCallback(() => {
    references.current.scrubInProgress = false;
    if (references.current.shouldPlayOnSeekingEnd) play();
    if (onSeekingEnd) onSeekingEnd();
  }, [onSeekingEnd, play]);

  const handleKeyDown = useCallback((event) => {
    if (event.keyCode === platform.keyCodes.navigation.left) {
      references.current.leftKeyPressed = true;
      if (references.current.blockBackwardScrubbing) return false;
    }
    if (event.keyCode === platform.keyCodes.navigation.right) {
      references.current.rightKeyPressed = true;
      if (references.current.blockForwardScrubbing) return false;
    }
    if (references.current.scrubInProgress) {
      if (event.keyCode === platform.keyCodes.mediaControl.pause) {
        references.current.shouldPlayOnSeekingEnd = false;
      } else if (event.keyCode === platform.keyCodes.mediaControl.play) {
        references.current.shouldPlayOnSeekingEnd = true;
      }
    }
    return onKeyDown(event);
  }, [onKeyDown]);

  // While scrubbing, if we reach the left most point we can scrub, we need to automatically
  // seek and start playback
  const handleSeekableStartReached = useCallback(() => {
    references.current.shouldPlayOnSeekingEnd = true;
    references.current.blockBackwardScrubbing = true;
    onSeek(references.current.seekableStart);
    if (onSeekableStartReached) onSeekableStartReached();
  }, [onSeek, onSeekableStartReached]);

  // While scrubbing, if we reach the right most point we can scrub, we need to automatically
  // seek and start playback
  const handleSeekableEndReached = useCallback(() => {
    references.current.shouldPlayOnSeekingEnd = true;
    references.current.blockForwardScrubbing = true;
    onSeek(references.current.seekableEnd);
    if (onSeekableEndReached) onSeekableEndReached();
  }, [onSeek, onSeekableEndReached]);

  // When seek is complete and the right key is not pressed, unblock forward scrubbing
  useEffect(() => {
    if (!seeking && !references.current.rightKeyPressed) {
      references.current.blockForwardScrubbing = false;
    }
  }, [seeking]);

  // Update seekableStart reference
  useEffect(() => {
    references.current.seekableStart = seekableStart;
  }, [seekableStart]);

  // Update seekableEnd reference
  useEffect(() => {
    references.current.seekableEnd = seekableEnd;
  }, [seekableEnd]);

  // When user releases left key, unblock backward scrubbing
  useOnKeyUp({
    listener: useCallback(() => {
      references.current.leftKeyPressed = false;
      references.current.blockBackwardScrubbing = false;
    }, []),
    forKeyCodes: platform.keyCodes.navigation.left,
    when: focused,
  });

  // When user releases right key, unblock forward scrubbing
  useOnKeyUp({
    listener: useCallback(() => {
      references.current.rightKeyPressed = false;
      references.current.blockForwardScrubbing = false;
    }, []),
    forKeyCodes: platform.keyCodes.navigation.right,
    when: focused,
  });

  return (
    <ProgressBar
      className={FocusableProgressBarClassNames.block({
        focused,
        scrubbing: isScrubbing,
      }, className)}
      progress={progress}
      duration={duration}
      seekable={seekable}
      scrubbing={scrubbing}
      seeking={seeking}
      flat
      focused={focused}
      hideMouseOver={!cursorVisible || seeking}
      onSeek={onSeek}
      onSeekingStart={handleSeekingStart}
      onSeekingEnd={handleSeekingEnd}
      onSeekableStartReached={handleSeekableStartReached}
      onSeekableEndReached={handleSeekableEndReached}
      onKeyDown={handleKeyDown}
    >
      {children}
    </ProgressBar>
  );
}

FocusableProgressBarBase.defaultProps = {
  className: '',
  progress: 0,
  duration: 0,
  seekable: undefined,
  scrubbing: undefined,
  isScrubbing: false,
  focused: false,
  cursorVisible: false,
  onSeek: undefined,
  onSeekingStart: undefined,
  onSeekingEnd: undefined,
  onSeekableStartReached: undefined,
  onSeekableEndReached: undefined,
  children: undefined,
};

FocusableProgressBarBase.propTypes = {
  className: PropTypes.string,
  progress: PropTypes.number,
  duration: PropTypes.number,
  seekable: PropTypes.shape({
    start: PropTypes.number,
    end: PropTypes.number,
  }),
  scrubbing: PropTypes.shape({
    direction: PropTypes.oneOf(Object.values(ScrubbingDirection)),
    speed: PropTypes.oneOf(Object.values(ScrubbingSpeed)),
  }),
  isScrubbing: PropTypes.bool,
  focused: PropTypes.bool,
  cursorVisible: PropTypes.bool,
  onSeek: PropTypes.func,
  onSeekingStart: PropTypes.func,
  onSeekingEnd: PropTypes.func,
  onSeekableStartReached: PropTypes.func,
  onSeekableEndReached: PropTypes.func,
  onKeyDown: PropTypes.func.isRequired,
  children: PropTypes.node,
};

const FocusableProgressBar = memo(withInteractiveFocusable()(memo(FocusableProgressBarBase)));

FocusableProgressBar.ProgressFill = withClassName(FocusableProgressBarClassNames.element('progress-fill'))(ProgressBar.ProgressFill);
FocusableProgressBar.ProgressDot = withClassName(FocusableProgressBarClassNames.element('progress-dot'))(ProgressBar.ProgressDot);
FocusableProgressBar.SeekableEndFill = withClassName(FocusableProgressBarClassNames.element('seekable-end-fill'))(ProgressBar.SeekableEndFill);
FocusableProgressBar.SeekableEndDot = withClassName(FocusableProgressBarClassNames.element('seekable-end-dot'))(ProgressBar.SeekableEndDot);
FocusableProgressBar.SeekableStartFill = withClassName(FocusableProgressBarClassNames.element('seekable-start-fill'))(ProgressBar.SeekableStartFill);
FocusableProgressBar.SeekableStartDot = withClassName(FocusableProgressBarClassNames.element('seekable-start-dot'))(ProgressBar.SeekableStartDot);
FocusableProgressBar.SeekingFill = withClassName(FocusableProgressBarClassNames.element('seeking-fill'))(ProgressBar.SeekingFill);
FocusableProgressBar.SeekingDot = withClassName(FocusableProgressBarClassNames.element('seeking-dot'))(ProgressBar.SeekingDot);
FocusableProgressBar.MouseOver = withClassName(FocusableProgressBarClassNames.element('mouse-over'))(ProgressBar.MouseOver);

export default FocusableProgressBar;
